import React, { ComponentPropsWithRef, forwardRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import {
  ReactComponentStaticProps,
  ReactDefaultPropsWithChildren,
} from "../../types/react";
import { Tile } from "../Tile";
import { CopyText } from "../CopyText";
import { EqualHeightElement } from "../EqualHeight/EqualHeightElement";

import { TileImageTeaserTileContent } from "./TileImageTeaserContent";
import { TileImageTeaserTileHeading } from "./TileImageTeaserTileHeading";
import { TileImageTeaserTileCopyText } from "./TileImageTeaserTileCopyText";
import { TileImageTeaserTileTextLink } from "./TileImageTeaserTileTextLink";
import { TileImageTeaserTileVisual } from "./TileImageTeaserVisual";
import { useTileImageTeaser } from "./TileImageTeaserContext";

import styles from "./TileImageTeaser.scss";

type TileImageTeaserTileBasePropsProps = {
  customClass?: string;
};

export type TileImageTeaserTileProps = TileImageTeaserTileBasePropsProps &
  Omit<
    React.ComponentPropsWithoutRef<"div">,
    keyof TileImageTeaserTileBasePropsProps
  >;

const defaultProps = {
  children: undefined,
  customClass: undefined,
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  TileImageTeaserTileBasePropsProps,
  typeof defaultProps
> = defaultProps;

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  customClass: PropTypes.string,
} as const;

export const TileImageTeaserTileWithRef = (
  props: TileImageTeaserTileProps,
  forwardedRef: ComponentPropsWithRef<"div">["ref"]
): JSX.Element => {
  const { children, customClass, ...restProps } = props;
  const { mode } = useTileImageTeaser();

  return (
    <Tile
      ref={forwardedRef}
      variant="shadow"
      customClass={cx(
        styles.tile,
        customClass,
        styles[`tile-mode-${mode}` as keyof typeof styles]
      )}
      {...restProps}
    >
      <EqualHeightElement name="TileImageTeaserTile">
        <CopyText tag="div">
          <div className={styles["tile-layout"]}>{children}</div>
        </CopyText>
      </EqualHeightElement>
    </Tile>
  );
};

export const TileImageTeaserTile = (forwardRef(
  TileImageTeaserTileWithRef
) as unknown) as ((
  props: TileImageTeaserTileProps
) => ReturnType<typeof TileImageTeaserTileWithRef>) &
  ReactComponentStaticProps<TileImageTeaserTileProps> & {
    Content: typeof TileImageTeaserTileContent;
    Heading: typeof TileImageTeaserTileHeading;
    CopyText: typeof TileImageTeaserTileCopyText;
    TextLink: typeof TileImageTeaserTileTextLink;
    Visual: typeof TileImageTeaserTileVisual;
  };

TileImageTeaserTile.propTypes = propTypes;
TileImageTeaserTile.defaultProps = validatedDefaultProps;

TileImageTeaserTile.Content = TileImageTeaserTileContent;
TileImageTeaserTile.Heading = TileImageTeaserTileHeading;
TileImageTeaserTile.CopyText = TileImageTeaserTileCopyText;
TileImageTeaserTile.TextLink = TileImageTeaserTileTextLink;
TileImageTeaserTile.Visual = TileImageTeaserTileVisual;

TileImageTeaserTile.displayName = "TileImageTeaser.Tile";

export default TileImageTeaserTile;
